import { redirect } from "react-router";
import type { Me } from "@repo/shared-types";
import { queries } from "./queries";
import { queryClient } from "./query";
import { loadSession } from "./session";

/**
 * Sends a signed-out visitor to the form instead of rendering a page that will
 * fail, and hands the page its session.
 *
 * A guard decides what someone sees, never what they may have: the api
 * authenticates every request on its own. A guard that renders a page for a
 * signed-out visitor is a bug about appearance, not a hole.
 */
export async function requireSession(request: Request): Promise<Me> {
  const session = await loadSession();
  if (!session) {
    const next = new URL(request.url).pathname;
    throw redirect(`/sign-in?next=${encodeURIComponent(next)}`);
  }
  return session;
}

/**
 * `requireSession`, then the page's own reads. While the cache holds somebody
 * signed in they are made beside the session rather than after it, so a
 * first visit waits once; otherwise after it, so nothing of a page is asked
 * for a visitor who is about to be sent to sign-in.
 */
export async function requireSessionWith<T>(request: Request, reads: () => Promise<T>): Promise<[Me, T]> {
  if (!queryClient.getQueryData(queries.me().queryKey)) {
    const session = await requireSession(request);
    return [session, await reads()];
  }
  const pending = reads();
  // Signed out after all: the guard's redirect wins, and nobody waits on these.
  pending.catch(() => {});
  const session = await requireSession(request);
  return [session, await pending];
}
